// store/integrations/action.ts
import { ActionCreator, Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { ApplicationState } from "../index";
import {
	FieldDTO,
	FieldMappingItem,
	FormDTO,
	IntegrationActionTypes,
	AccountDTO,
	MappingRecord
} from "./types";
import config from "../../config";
import { responseHandler } from "utils";

const API = config.api.DTS_MARKETPLACE_API;
type AppThunk = ActionCreator<ThunkAction<void, ApplicationState, null, Action<string>>>;


// 1. LIST APPS
export const listAppsRequest: AppThunk = (tenantId?: String, onSuccess?, onError?) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.GET_APPS_REQUEST });
		const token = getState().auth.token;
		const res = await fetch(
			`${API}/api/apps?tenantId=${tenantId}`,
			{
				headers: {
					"Content-Type": "application/json",
					...(token && { token })
				}
			}
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_APPS_SUCCESS,
			IntegrationActionTypes.GET_APPS_ERROR,
			null,
			onSuccess,
			onError,
			undefined,
			true
		);
	};
};


// 2. GET SINGLE APP
export const getAppRequest: AppThunk = (tenantId: string, appId: string) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.GET_APP_REQUEST });
		const token = getState().auth.token;
		const res = await fetch(
			`${API}/api/apps/${appId}?tenantId=${tenantId}`,
			{
				headers: {
					"Content-Type": "application/json",
					...(token && { token })
				}
			}
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_APP_SUCCESS,
			IntegrationActionTypes.GET_APP_ERROR,
			null,
			null,
			null,
			undefined,
			true
		);
	};
};


// 3. LIST VERSIONS
export const listVersionsRequest: AppThunk = (appId: string) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.GET_VERSIONS_REQUEST });
		const token = getState().auth.token;
		const res = await fetch(`${API}/api/apps/${appId}/versions`, {
			headers: {
				"Content-Type": "application/json",
				...(token && { token })
			}
		});
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_VERSIONS_SUCCESS,
			IntegrationActionTypes.GET_VERSIONS_ERROR,
			null,
			null,
			null,
			undefined,
			true
		);
	};
};


// 4. LIST INSTALLATIONS
export const installationsGetRequest: AppThunk = (tenantId, onSuccess?, onError?) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.GET_INSTALLS_REQUEST });
		const token = getState().auth.token;
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations`,
			{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_INSTALLS_SUCCESS,
			IntegrationActionTypes.GET_INSTALLS_ERROR,
			null,
			onSuccess,
			onError,
			undefined,
			true
		);
	};
};


// 5. UNINSTALL
export const uninstallRequest: AppThunk = (tenantId, installationId, onSuccess?, onError?) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.DELETE_INSTALL_REQUEST });
		const token = getState().auth.token;
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}`,
			{ method: "DELETE", headers: { ...(token && { token }) } }
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.DELETE_INSTALL_SUCCESS,
			IntegrationActionTypes.DELETE_INSTALL_ERROR,
			{ 204: { message: "Uninstalled", success: true } },
			onSuccess,
			onError
		);
	};
};


// 6. PHASE A (OAuth) → initiate (no DB write)
export const oauthInitiateRequest: AppThunk = (
	tenantId: string,
	appId: string,
	onSuccess: (redirectUrl: string) => void,
	onError?: (err: any) => void
) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.INIT_OAUTH_REQUEST });
		const token = getState().auth.token;
		try {
			const res = await fetch(
				`${API}/api/tenants/${tenantId}/installations/apps/${appId}/oauth/initiate`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json", ...(token && { token }) }
				}
			);
			const { redirect_url } = await res.json();
			dispatch({ type: IntegrationActionTypes.INIT_OAUTH_SUCCESS });
			onSuccess(redirect_url);
		} catch (err) {
			dispatch({ type: IntegrationActionTypes.INIT_OAUTH_ERROR, error: err as any });
			onError?.(err);
		}
	};
};


// 7. INSTALL (API-Key/manual flows)
export const installRequest: AppThunk = (
	tenantId: string,
	appVersionId: string,
	name: string,
	configJson: string,
	onSuccess?: (dto: any) => void,
	onError?: (err: any) => void
) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.POST_INSTALL_REQUEST });
		const token = getState().auth.token;
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations`,
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token && { token })
				},
				body: JSON.stringify({ app_version_id: appVersionId, name, config_json: configJson })
			}
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.POST_INSTALL_SUCCESS,
			IntegrationActionTypes.POST_INSTALL_ERROR,
			null,
			onSuccess,
			onError
		);
	};
};


// 8. COMPLETE OAUTH → callback then persist install
export const completeOAuthRequest: AppThunk = (
	tenantId: string,
	appId: string,
	code: string,
	onSuccess?: (dto: any) => void,
	onError?: (err: any) => void
) => {
	return async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.COMPLETE_OAUTH_REQUEST });
		const token = getState().auth.token;
		try {
			const res = await fetch(
				`${API}/api/tenants/${tenantId}/installations/apps/${appId}/oauth/callback?code=${encodeURIComponent(code)}`,
				{ headers: { ...(token && { token }) } }
			);
			const body = await res.json();
			dispatch({ type: IntegrationActionTypes.COMPLETE_OAUTH_SUCCESS, payload: body });
			onSuccess?.(body);
		} catch (err) {
			dispatch({ type: IntegrationActionTypes.COMPLETE_OAUTH_ERROR, error: err as any });
			onError?.(err);
		}
	};
};

// ––––– 0. GET METADATA –––––
export const getMetadataRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	onError?: (err: any) => void
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_METADATA_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/metadata`,
			{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
		);
		const json = await res.json();
		dispatch({ type: IntegrationActionTypes.GET_METADATA_SUCCESS, payload: json });
	} catch (err: any) {
		dispatch({ type: IntegrationActionTypes.GET_METADATA_ERROR, error: err });
		onError?.(err);
	}
};

// 9. PAGES
export const accountsGetRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	onSuccess?: (pages: AccountDTO[]) => void,
	onError?: (err: any) => void
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_ACCOUNTS_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/accounts`,
			{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_ACCOUNTS_SUCCESS,
			IntegrationActionTypes.GET_ACCOUNTS_ERROR,
			null,
			onSuccess,
			onError
		);
	} catch (err) {
		dispatch({ type: IntegrationActionTypes.GET_ACCOUNTS_ERROR, error: err as any });
		onError?.(err);
	}
};

// 9. PAGES
export const pagesGetRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	onSuccess?: (pages: AccountDTO[]) => void,
	onError?: (err: any) => void
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_PAGES_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/pages`,
			{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_PAGES_SUCCESS,
			IntegrationActionTypes.GET_PAGES_ERROR,
			null,
			onSuccess,
			onError
		);
	} catch (err) {
		dispatch({ type: IntegrationActionTypes.GET_PAGES_ERROR, error: err as any });
		onError?.(err);
	}
};

// 10. FORMS
export const formsGetRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	pageId: string,
	pageAccessToken: string,
	onSuccess?: (forms: FormDTO[]) => void,
	onError?: (err: any) => void
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_FORMS_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/accounts/${pageId}/forms?access_token=${encodeURIComponent(pageAccessToken)}`,
			{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
		);
		await responseHandler(
			res,
			dispatch,
			IntegrationActionTypes.GET_FORMS_SUCCESS,
			IntegrationActionTypes.GET_FORMS_ERROR,
			null,
			onSuccess,
			onError
		);
	} catch (err) {
		dispatch({ type: IntegrationActionTypes.GET_FORMS_ERROR, error: err as any });
		onError?.(err);
	}
};

// 12. GET FORM FIELDS
export const fieldsGetRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	formId: string
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_FIELDS_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/forms/${formId}/fields`,
			{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
		);
		const data: FieldDTO[] = await res.json();
		dispatch({ type: IntegrationActionTypes.GET_FIELDS_SUCCESS, payload: data });
	} catch (err: any) {
		dispatch({ type: IntegrationActionTypes.GET_FIELDS_ERROR, error: err });
	}
};

/**
 * Generic discovery thunk: calls
 *   GET /api/tenants/:t/installations/:i/discovery/:opName?[query]
 */
export const discoveryRequest: AppThunk = (
	opName: string,
	tenantId: string,
	installationId: string,
	query: Record<string, string> = {}
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_DISCOVERY_REQUEST, meta: { opName } });

	const base = `${API}/api/tenants/${tenantId}/installations/${installationId}/discovery/${opName}`;
	const url = Object.keys(query).length
		? base + "?" + new URLSearchParams(query).toString()
		: base;

	const token = getState().auth.token;
	const res = await fetch(url, {
		headers: { "Content-Type": "application/json", ...(token && { token }) }
	});

	if (!res.ok) {
		const errText = await res.text();
		dispatch({
			type: IntegrationActionTypes.GET_DISCOVERY_ERROR,
			error: errText,
			meta: { opName }
		});
		return;
	}

	 const data = await res.json();
	 // always produce { records, template }
	 const records = Array.isArray(data)
	   ? data
	   : Array.isArray((data as any).records)
	     ? (data as any).records
	     : [];
	 const template = (data as any).template;
	 dispatch({
	   type: IntegrationActionTypes.GET_DISCOVERY_SUCCESS,
	   meta: { opName },
	   payload: { records, template }
	 });
};

// 14. SAVE MAPPING
export const mappingSaveRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	mapping: FieldMappingItem[],
	onSuccess?: () => void,
	onError?: (e: any) => void
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.SAVE_MAPPING_REQUEST });
	const token = getState().auth.token;
	try {
		await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/mapping`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json", ...(token && { token }) },
				body: JSON.stringify(mapping)
			}
		);
		dispatch({ type: IntegrationActionTypes.SAVE_MAPPING_SUCCESS, payload: mapping });
		onSuccess?.();
	} catch (err: any) {
		dispatch({ type: IntegrationActionTypes.SAVE_MAPPING_ERROR, error: err });
		onError?.(err);
	}
};

// 1. List all saved mappings
export const mappingGetByAppRequest: AppThunk = (tenantId, appId) =>
	async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.GET_MAPPING_RECORDS_REQUEST });
		const token = getState().auth.token;
		try {
			const res = await fetch(
				`${API}/api/tenants/${tenantId}/installations/apps/${appId}/mappings`,
				{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
			);
			const data: MappingRecord[] = await res.json();
			dispatch({
				type: IntegrationActionTypes.GET_MAPPING_RECORDS_SUCCESS,
				payload: data,
			});
		} catch (err: any) {
			dispatch({
				type: IntegrationActionTypes.GET_MAPPING_RECORDS_ERROR,
				error: err,
			});
		}
};

// 1. List all saved mappings
export const mappingGetByInstallRequest: AppThunk = (tenantId, installId) =>
	async (dispatch, getState) => {
		dispatch({ type: IntegrationActionTypes.GET_MAPPING_RECORDS_REQUEST });
		const token = getState().auth.token;
		try {
			const res = await fetch(
				`${API}/api/tenants/${tenantId}/installations/${installId}/mappings`,
				{ headers: { "Content-Type": "application/json", ...(token && { token }) } }
			);
			const data: MappingRecord[] = await res.json();
			dispatch({
				type: IntegrationActionTypes.GET_MAPPING_RECORDS_SUCCESS,
				payload: data,
			});
		} catch (err: any) {
			dispatch({
				type: IntegrationActionTypes.GET_MAPPING_RECORDS_ERROR,
				error: err,
			});
		}
};

// fetch a single MappingRecord by ID
export const mappingGetRecordRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	recordId: string
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.GET_MAPPING_RECORD_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/mappings/${recordId}`,
			{
				headers: {
					"Content-Type": "application/json",
					...(token && { token }),
				},
			}
		);
		if (!res.ok) throw new Error(`${res.status}`);
		const rec: MappingRecord = await res.json();
		dispatch({
			type: IntegrationActionTypes.GET_MAPPING_RECORD_SUCCESS,
			payload: rec,
		});
	} catch (err: any) {
		dispatch({
			type: IntegrationActionTypes.GET_MAPPING_RECORD_ERROR,
			error: err.message ?? err.toString(),
		});
	}
};


// 2. Create a new mapping record
export const mappingCreateRequest: AppThunk = (
	tenantId, installationId,
	name: string,
	config: Record<string, any>,
	onSuccess?, onError?
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.CREATE_MAPPING_RECORD_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/mappings`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json", ...(token && { token }) },
				body: JSON.stringify({ name, config })
			}
		);
		const rec: MappingRecord = await res.json();
		dispatch({ type: IntegrationActionTypes.CREATE_MAPPING_RECORD_SUCCESS, payload: rec });
		onSuccess?.();
	} catch (err: any) {
		dispatch({ type: IntegrationActionTypes.CREATE_MAPPING_RECORD_ERROR, error: err });
		onError?.(err);
	}
};

// 3. Update an existing mapping record
export const mappingUpdateRequest: AppThunk = (
	tenantId, installationId,
	recordId: string,
	name: string,
	config: Record<string, any>,
	onSuccess?, onError?
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.UPDATE_MAPPING_RECORD_REQUEST });
	const token = getState().auth.token;
	try {
		const res = await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/mappings/${recordId}`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json", ...(token && { token }) },
				body: JSON.stringify({ name, config })
			}
		);
		const rec: MappingRecord = await res.json();
		dispatch({ type: IntegrationActionTypes.UPDATE_MAPPING_RECORD_SUCCESS, payload: rec });
		onSuccess?.();
	} catch (err: any) {
		dispatch({ type: IntegrationActionTypes.UPDATE_MAPPING_RECORD_ERROR, error: err });
		onError?.(err);
	}
};

// 4. Delete a mapping record
export const mappingDeleteRequest: AppThunk = (
	tenantId, installationId,
	recordId: string,
	onSuccess?, onError?
) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.DELETE_MAPPING_RECORD_REQUEST });
	const token = getState().auth.token;
	try {
		await fetch(
			`${API}/api/tenants/${tenantId}/installations/${installationId}/mappings/${recordId}`,
			{ method: "DELETE", headers: { ...(token && { token }) } }
		);
		dispatch({ type: IntegrationActionTypes.DELETE_MAPPING_RECORD_SUCCESS, payload: recordId });
		onSuccess?.();
	} catch (err: any) {
		dispatch({ type: IntegrationActionTypes.DELETE_MAPPING_RECORD_ERROR, error: err });
		onError?.(err);
	}
};


// then, after your other thunks, add:
export const sendOperationRequest: AppThunk = (
	tenantId: string,
	installationId: string,
	opName: string,
	context: any,
	onSuccess?: () => void,
	onError?: (e: any) => void
  ) => async (dispatch, getState) => {
	dispatch({ type: IntegrationActionTypes.INVOKE_OPERATION_REQUEST });
	const token = getState().auth.token;
	try {
	  const res = await fetch(
		`${API}/api/tenants/${tenantId}/installations/${installationId}/operations/${opName}`,
		{
		  method: "POST",
		  headers: {
			"Content-Type": "application/json",
			...(token && { token })
		  },
		  body: JSON.stringify(context)
		}
	  );
	  // you can reuse responseHandler if you want to normalize error → success
	  if (!res.ok) throw new Error(`HTTP ${res.status}`);
	  const json = await res.json();
	  dispatch({ type: IntegrationActionTypes.INVOKE_OPERATION_SUCCESS, payload: json });
	  onSuccess?.();
	} catch (err: any) {
	  dispatch({ type: IntegrationActionTypes.INVOKE_OPERATION_ERROR, error: err.message });
	  onError?.(err);
	}
  };

  //  QUOTA DETAILS
export const getAppQuotaRequest: AppThunk = (tenantId: string, appId: string, onSuccess?: (quota: any) => void, onError?: (err: any) => void) => {
	return async (dispatch, getState) => {
	  dispatch({ type: IntegrationActionTypes.GET_QUOTA_REQUEST });
	  const token = getState().auth.token;
	  try {
		const res = await fetch(
		  `${config.api.DTS_MARKETPLACE_API}/api/tenants/${tenantId}/apps/${appId}/quota`,
		  {
			headers: { "Content-Type": "application/json", ...(token && { token }) }
		  }
		);
		const json = await res.json();
		dispatch({ type: IntegrationActionTypes.GET_QUOTA_SUCCESS, payload: json });
		onSuccess?.(json);
	  } catch (err) {
		dispatch({ type: IntegrationActionTypes.GET_QUOTA_ERROR, error: err });
		onError?.(err);
	  }
	};
  };
  

